import React, { useState, useEffect } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from './ui/dialog';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { Switch } from './ui/switch';
import { Plus, Trash2, GripVertical, Users } from 'lucide-react';
import api from '../api/api';
import { toast } from 'sonner';

// Only the application's real User-Management roles are assignable to workflow steps.
const VALID_ROLES = ['CompanyAdmin', 'RecordsManager', 'Approver', 'Contributor', 'Reader'];
const DOC_TYPES = ['Invoice', 'Contract', 'Policy', 'Technical Manual', 'Report', 'Other'];

const emptyStep = () => ({ name: '', allowed_roles: [], sla_hours: 24, parallel: false });

/**
 * Create / Edit a workflow definition.
 * Pass `workflow` to edit an existing one, or null to create a new one.
 */
export const WorkflowDefinitionDialog = ({ open, onOpenChange, workflow, headers, onSaved }) => {
  const isEdit = Boolean(workflow?.id);
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [isActive, setIsActive] = useState(true);
  const [slaHours, setSlaHours] = useState(48);
  const [docTypes, setDocTypes] = useState([]);
  const [steps, setSteps] = useState([emptyStep()]);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open) return;
    if (isEdit) {
      setName(workflow.name || '');
      setDescription(workflow.description || '');
      setIsActive(workflow.is_active !== false);
      setSlaHours(workflow.sla_hours || 48);
      setDocTypes(workflow.document_types || []);
      const wfSteps = (workflow.json_definition?.steps || []).map((s) => ({
        name: s.name || '',
        allowed_roles: s.allowed_roles?.length ? s.allowed_roles : (s.approvers || []),
        sla_hours: s.sla_hours || 24,
        parallel: Boolean(s.parallel),
      }));
      setSteps(wfSteps.length ? wfSteps : [emptyStep()]);
    } else {
      setName(''); setDescription(''); setIsActive(true); setSlaHours(48); setDocTypes([]);
      setSteps([
        { name: 'RecordsManager Review', allowed_roles: ['RecordsManager'], sla_hours: 24, parallel: false },
        { name: 'Approver Review', allowed_roles: ['Approver'], sla_hours: 24, parallel: false },
        { name: 'CompanyAdmin Final Approval', allowed_roles: ['CompanyAdmin'], sla_hours: 24, parallel: false },
      ]);
    }
    // eslint-disable-next-line
  }, [open, workflow]);

  const updateStep = (idx, patch) =>
    setSteps((prev) => prev.map((s, i) => (i === idx ? { ...s, ...patch } : s)));

  const toggleStepRole = (idx, role) =>
    setSteps((prev) => prev.map((s, i) => {
      if (i !== idx) return s;
      const has = s.allowed_roles.includes(role);
      return { ...s, allowed_roles: has ? s.allowed_roles.filter((r) => r !== role) : [...s.allowed_roles, role] };
    }));

  const addStep = () => setSteps((prev) => [...prev, emptyStep()]);
  const removeStep = (idx) => setSteps((prev) => (prev.length > 1 ? prev.filter((_, i) => i !== idx) : prev));

  const toggleDocType = (dt) =>
    setDocTypes((prev) => (prev.includes(dt) ? prev.filter((x) => x !== dt) : [...prev, dt]));

  const validate = () => {
    if (!name.trim()) { toast.error('Workflow name is required'); return false; }
    for (let i = 0; i < steps.length; i++) {
      if (!steps[i].name.trim()) { toast.error(`Step ${i + 1} needs a name`); return false; }
      if (!steps[i].allowed_roles.length) { toast.error(`Step "${steps[i].name || i + 1}" needs at least one role`); return false; }
    }
    return true;
  };

  const handleSave = async () => {
    if (!validate()) return;
    const payload = {
      name: name.trim(),
      description: description.trim() || null,
      is_active: isActive,
      sla_hours: Number(slaHours) || null,
      document_types: docTypes,
      json_definition: {
        steps: steps.map((s) => ({
          name: s.name.trim(),
          allowed_roles: s.allowed_roles,
          approvers: s.allowed_roles,
          sla_hours: Number(s.sla_hours) || null,
          parallel: s.parallel,
        })),
      },
    };
    try {
      setSaving(true);
      if (isEdit) {
        await api.updateWorkflow(workflow.id, payload, headers);
        toast.success('Workflow updated successfully');
      } else {
        await api.createWorkflow(payload, headers);
        toast.success('Workflow created successfully');
      }
      onSaved?.();
      onOpenChange(false);
    } catch (e) {
      toast.error(e.response?.data?.detail || `Failed to ${isEdit ? 'update' : 'create'} workflow`);
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="workflow-definition-dialog" className="max-w-2xl max-h-[88vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{isEdit ? 'Edit Workflow' : 'Create Workflow'}</DialogTitle>
          <DialogDescription>
            Define the approval steps and the application roles allowed to act on each step.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-5 py-2">
          {/* Basics */}
          <div className="grid grid-cols-1 gap-3">
            <div>
              <label className="block text-sm font-medium mb-1">Workflow Name *</label>
              <Input data-testid="wf-name-input" value={name} onChange={(e) => setName(e.target.value)} placeholder="e.g. Standard Document Approval" />
            </div>
            <div>
              <label className="block text-sm font-medium mb-1">Description</label>
              <Textarea data-testid="wf-description-input" value={description} onChange={(e) => setDescription(e.target.value)} rows={2} placeholder="What is this workflow for?" />
            </div>
            <div className="flex items-center gap-6">
              <div className="flex items-center gap-2">
                <label className="text-sm font-medium">SLA (hours)</label>
                <Input data-testid="wf-sla-input" type="number" min="1" value={slaHours} onChange={(e) => setSlaHours(e.target.value)} className="w-24" />
              </div>
              <div className="flex items-center gap-2">
                <Switch data-testid="wf-active-switch" checked={isActive} onCheckedChange={setIsActive} />
                <span className="text-sm font-medium">{isActive ? 'Active' : 'Inactive'}</span>
              </div>
            </div>
          </div>

          {/* Document types */}
          <div>
            <label className="block text-sm font-medium mb-2">Supported Document Types</label>
            <div className="flex flex-wrap gap-2" data-testid="wf-doctypes">
              {DOC_TYPES.map((dt) => (
                <button
                  key={dt}
                  type="button"
                  data-testid={`wf-doctype-${dt.replace(/\s+/g, '-')}`}
                  onClick={() => toggleDocType(dt)}
                  className={`px-3 py-1 rounded-full text-xs border transition-colors ${docTypes.includes(dt) ? 'bg-blue-600 text-white border-blue-600' : 'bg-white text-slate-600 border-slate-300 hover:border-blue-400'}`}
                >
                  {dt}
                </button>
              ))}
            </div>
          </div>

          {/* Steps */}
          <div>
            <div className="flex items-center justify-between mb-2">
              <label className="text-sm font-medium">Workflow Steps *</label>
              <Button type="button" variant="outline" size="sm" onClick={addStep} data-testid="wf-add-step-btn">
                <Plus className="w-4 h-4 mr-1" /> Add Step
              </Button>
            </div>
            <div className="space-y-3">
              {steps.map((step, idx) => (
                <div key={idx} data-testid={`wf-step-${idx}`} className="border rounded-lg p-3 bg-slate-50">
                  <div className="flex items-center gap-2 mb-2">
                    <GripVertical className="w-4 h-4 text-slate-400" />
                    <span className="w-6 h-6 rounded-full bg-blue-600 text-white flex items-center justify-center text-xs font-semibold">{idx + 1}</span>
                    <Input
                      data-testid={`wf-step-name-${idx}`}
                      value={step.name}
                      onChange={(e) => updateStep(idx, { name: e.target.value })}
                      placeholder="Step name (e.g. RecordsManager Review)"
                      className="flex-1"
                    />
                    <Input
                      data-testid={`wf-step-sla-${idx}`}
                      type="number" min="1"
                      value={step.sla_hours}
                      onChange={(e) => updateStep(idx, { sla_hours: e.target.value })}
                      className="w-20"
                      title="Step SLA (hours)"
                    />
                    <button type="button" onClick={() => removeStep(idx)} data-testid={`wf-step-remove-${idx}`} className="text-red-500 hover:text-red-700 p-1">
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                  <div className="pl-8">
                    <div className="flex items-center gap-1 text-xs text-slate-500 mb-1">
                      <Users className="w-3 h-3" /> Allowed Roles:
                    </div>
                    <div className="flex flex-wrap gap-2">
                      {VALID_ROLES.map((role) => (
                        <button
                          key={role}
                          type="button"
                          data-testid={`wf-step-${idx}-role-${role}`}
                          data-selected={step.allowed_roles.includes(role)}
                          onClick={() => toggleStepRole(idx, role)}
                          className={`px-2.5 py-1 rounded-full text-xs border transition-colors ${step.allowed_roles.includes(role) ? 'bg-emerald-600 text-white border-emerald-600' : 'bg-white text-slate-600 border-slate-300 hover:border-emerald-400'}`}
                        >
                          {role}
                        </button>
                      ))}
                    </div>
                    <label className="flex items-center gap-2 mt-2 text-xs text-slate-600">
                      <input type="checkbox" checked={step.parallel} onChange={(e) => updateStep(idx, { parallel: e.target.checked })} data-testid={`wf-step-parallel-${idx}`} />
                      Parallel approval (any selected role can approve independently)
                    </label>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={saving} data-testid="wf-cancel-btn">Cancel</Button>
          <Button onClick={handleSave} disabled={saving} data-testid="wf-save-btn" className="bg-blue-600 hover:bg-blue-700 text-white">
            {saving ? 'Saving...' : isEdit ? 'Save Changes' : 'Create Workflow'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default WorkflowDefinitionDialog;
